<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import ChartWidget from '@/components/dashboard/ChartWidget.vue';
import SingleNumberWidget from '@/components/dashboard/SingleNumberWidget.vue';
import ParticipationTable from '@/components/dashboard/ParticipationTable.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import HomepageItemSelectionButton from '@/components/HomepageItemSelectionButton.vue';
import RoleVisibilityGuard from '@/components/RoleVisibilityGuard.vue';

import { getKeyDashboardMetrics, getVisitorsSeries, getWeeklyViewsChartOptions } from '@/composables/dashboard-metrics';
import { useCommunityStore, useDashboardStore, useProjectStore, useConsultationsStore, useUsersStore } from '@/stores';
import { Constants } from '@/constants';
import { getLanguageEnabled } from '@/composables/data-display-utils';

// STORE
const communityStore = useCommunityStore();
const dashboardStore = useDashboardStore();
const projectStore = useProjectStore();
const consultationStore = useConsultationsStore();
const userStore = useUsersStore();
const { community } = storeToRefs(communityStore);

const { t } = useI18n();

// CONST
const dashboardMetrics = ref();
const chartOptionsData = ref();
const response = ref();
const languageEnabled = computed(() => getLanguageEnabled(community.value));

// FORM
const { handleSubmit, meta, resetForm, values, setFieldValue } = useForm({
  initialValues: {
    filterType: null,
    homepageItemId: null,
    timePeriod: Constants.Dashboard.TimePeriod.LastSevenDays,
  },
  validationSchema: yup.object({
    filterType: yup.number().when([], {
      is: () => userStore.currentUser?.role === Constants.User.Role.EDITOR,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    homepageItemId: yup.number().when('filterType', {
      is: null,
      then: (schema) => schema.nullable().optional(),
      otherwise: (schema) => schema.required(),
    }),
    timePeriod: yup.number().required(),
  }),
});

const onSubmit = handleSubmit(async (values) => {
  if (values.filterType == null) {
    return;
  }

  switch (values.filterType) {
    case Constants.Dashboard.Type.Project:
      response.value = await projectStore.fetchProjectStats(values.homepageItemId!, values.timePeriod);
      break;
    case Constants.Dashboard.Type.Consultation:
      response.value = await consultationStore.fetchConsultationStats(values.homepageItemId!, values.timePeriod);
      break;
    default:
      response.value = await communityStore.fetchCommunityStats(communityId.value, values.timePeriod);
      break;
  }

  dashboardMetrics.value = getKeyDashboardMetrics(response.value.data);
  chartOptionsData.value = getWeeklyViewsChartOptions(response.value.data);
});

// LIFECYCLE
onMounted(async () => {
  if (isAdmin()) {
    await getCommunityStats(Constants.Dashboard.TimePeriod.LastSevenDays);
  }
});

// WATCH
watch(
  () => values.filterType,
  (newValue: number | null, oldValue: number | null) => {
    if (newValue !== oldValue) {
      clearHomepageItemId();
    }
  },
);

// WIDGET FUNCTIONS
const widgetType = (type: string) => {
  const componentMap: { [key: string]: any } = {
    SingleNumberWidget,
    ChartWidget,
  };
  return componentMap[type] || null;
};

const widgetColumns = (type: string) => {
  if (type === 'ChartWidget') {
    return 'col-12';
  } else {
    return 'col-4';
  }
};

// COMPUTED
const communityId = computed(() => {
  return communityStore.community!.id;
});

const seriesData = computed(() => {
  if (response.value) {
    return [
      {
        name: t('dashboard.views'),
        data: getVisitorsSeries(response.value.data),
      },
    ];
  } else {
    return [];
  }
});

const widgetsList = computed(() => {
  if (response.value) {
    return [
      {
        id: 1,
        type: 'SingleNumberWidget',
        props: { number: dashboardMetrics.value.visitors, title: t('dashboard.visitors') },
      },
      {
        id: 2,
        type: 'SingleNumberWidget',
        props: { number: dashboardMetrics.value.visits, title: t('dashboard.visits') },
      },
      {
        id: 3,
        type: 'SingleNumberWidget',
        props: { number: dashboardMetrics.value.pageviews, title: t('dashboard.total-views') },
      },
      {
        id: 4,
        type: 'ChartWidget',
        props: {
          type: 'bar',
          data: seriesData.value,
          chartOptions: chartOptionsData.value,
          title: t('dashboard.visitors'),
        },
      },
    ];
  } else {
    return [];
  }
});

const timePeriodOptions = computed(() => dashboardStore.getTimePeriods(t));
const typeOptions = computed(() => dashboardStore.getTypes(t));

// FUNCTIONS
function onItemSelection(row: any) {
  setFieldValue('homepageItemId', row.id);
}

function updateTimePeriod(newValue: number) {
  setFieldValue('timePeriod', newValue);
}

async function clearFilters() {
  resetForm();
  if (isAdmin()) {
    await getCommunityStats(Constants.Dashboard.TimePeriod.LastSevenDays);
  } else {
    response.value = null;
  }
}

function clearHomepageItemId() {
  setFieldValue('homepageItemId', null);
}

async function getCommunityStats(timePeriod: number) {
  response.value = await communityStore.fetchCommunityStats(communityId.value, timePeriod);
  dashboardMetrics.value = getKeyDashboardMetrics(response.value.data);
  chartOptionsData.value = getWeeklyViewsChartOptions(response.value.data);
}

function isAdmin() {
  return userStore.currentUser?.role === Constants.User.Role.ADMIN;
}
</script>

<template>
  <q-page class="dashboard">
    <div class="dashboard__stat-filter">
      <h3>{{ t('dashboard.label.visit-stats') }}</h3>
      <h6>{{ t('dashboard.label.filter-by') }}</h6>
      <div class="options">
        <localized-select
          name="filterType"
          :label="t('dashboard.label.type')"
          :options="typeOptions"
          :clearable="true"
          emit-value
          map-options
          outlined
          hide-bottom-space
        />
        <HomepageItemSelectionButton
          value="homepageItemId"
          @selection="onItemSelection"
          @clear="clearHomepageItemId"
          :hint="t('dashboard.label.select-type')"
          :item-type="values.filterType"
          :disabled="!values.filterType"
          :language-enabled="languageEnabled"
        />
        <localized-select
          name="timePeriod"
          :label="t('dashboard.label.stat-time-period')"
          :options="timePeriodOptions"
          emit-value
          map-options
          outlined
          hide-bottom-space
          @change="updateTimePeriod"
        />
        <div class="options__actions">
          <q-btn flat text-color="primary" color="white" :label="t('global.reset')" @click="clearFilters" />
          <q-btn color="primary" :disable="!meta.valid" :label="t('global.submit')" @click="onSubmit" />
        </div>
      </div>
    </div>
    <div v-if="!response" class="no-filter-selected">
      {{ t('dashboard.label.no-filter-selected') }}
    </div>
    <div class="row">
      <div v-for="widget in widgetsList" :class="widgetColumns(widget.type)" :key="widget.id">
        <component :is="widgetType(widget.type)" v-bind="widget.props" />
      </div>
    </div>
    <role-visibility-guard :roles-allowed="[Constants.User.Role.ADMIN]">
      <div class="dashboard__participation-stats q-mt-lg">
        <h3>{{ t('dashboard.label.participation-stats') }}</h3>
        <div class="participation" v-if="community?.summary?.stats">
          <ParticipationTable
            title="Participation"
            :data="community.summary.stats"
            :language-enabled="languageEnabled"
          />
        </div>
      </div>
    </role-visibility-guard>
  </q-page>
</template>

<style lang="scss">
.dashboard {
  display: flex;
  flex-direction: column;
  padding: 16px;

  &__title {
    display: flex;

    h1 {
      margin: 0 10px !important;
    }
  }

  &__stat-filter {
    display: flex;
    flex-direction: column;
    max-width: 500px;
    margin: 10px;

    h6 {
      margin: 0;
    }

    .options {
      display: flex;
      flex-direction: column;
      gap: 20px;
      width: 100%;

      &__actions {
        display: flex;
        flex-direction: row;
        justify-content: end;
        gap: 20px;
      }

      .q-btn {
        height: 40px;
      }
    }
  }

  &__participation-stats {
    h3 {
      padding: 8px 16px;
    }

    .participation {
      width: inherit;
    }
  }

  .no-filter-selected {
    text-align: center;
    background-color: $color-neutral-grey-200;
    border-radius: 8px;
    padding: 16px;
    color: $color-neutral-black-text;
    margin-top: 10px;
  }
}
</style>
